import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, ClipboardList, LayoutGrid, List, Eye, EyeOff, Settings } from 'lucide-react'
import { supportTaskApi } from '../services/api'
import { showError } from '../utils/showError'
import KanbanBoard from '../components/KanbanBoard'
import {
  PageHeader,
  SearchBar,
  ListContainer,
  Pagination,
  LoadingSpinner,
  EmptyState,
  Badge,
  Button,
  Card,
  CardBody,
  FormSelect,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from '../components/common'

const statusLabels = { 1: 'Open', 2: 'In Progress', 3: 'On Hold', 4: 'Resolved', 5: 'Closed' }
const statusColors = { 1: 'info', 2: 'warning', 3: 'default', 4: 'success', 5: 'default' }
const priorityLabels = { 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Critical' }
const priorityColors = { 1: 'default', 2: 'warning', 3: 'danger', 4: 'danger' }
const typeLabels = { 1: 'Bug', 2: 'Feature', 3: 'Improvement', 4: 'Maintenance', 5: 'Investigation', 6: 'Other' }

const scopeFlags = (scope) => {
  const parts = []
  if (scope & 1) parts.push('API')
  if (scope & 2) parts.push('Mobile')
  if (scope & 4) parts.push('Portal')
  return parts.join(', ') || '-'
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const Tasks = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const pageSize = 10
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('tasks_viewMode') || 'table')
  const [kanbanData, setKanbanData] = useState(null)
  const [kanbanLoading, setKanbanLoading] = useState(false)
  const [filters, setFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tasks_filters') || '{}') } catch { return {} }
  })
  const [showClosed, setShowClosed] = useState(() => localStorage.getItem('tasks_showClosed') === 'true')
  const [assigneeOptions, setAssigneeOptions] = useState([])

  useEffect(() => { localStorage.setItem('tasks_viewMode', viewMode) }, [viewMode])
  useEffect(() => { localStorage.setItem('tasks_filters', JSON.stringify(filters)) }, [filters])
  useEffect(() => { localStorage.setItem('tasks_showClosed', String(showClosed)) }, [showClosed])

  useEffect(() => {
    loadAssignees()
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput)
        setCurrentPage(1)
      }
    }, 500)
    return () => clearTimeout(timeout)
  }, [searchInput, searchTerm])

  useEffect(() => {
    if (viewMode === 'table') {
      loadItems()
    } else {
      loadKanban()
    }
  }, [currentPage, searchTerm, filters, viewMode, showClosed])

  const loadAssignees = async () => {
    try {
      const response = await supportTaskApi.getMembers()
      setAssigneeOptions(response.data || [])
    } catch (error) {
      console.error('Error loading task members:', error)
    }
  }

  const loadItems = async () => {
    try {
      setLoading(true)
      // When user explicitly selected Closed status filter, don't exclude closed
      const hasExplicitClosedFilter = filters.status === '5' || filters.status === 5
      const params = {
        pageNumber: currentPage,
        pageSize,
        ...(searchTerm && { searchString: searchTerm }),
        ...filters,
        ...(!showClosed && !hasExplicitClosedFilter ? { excludeClosed: true } : {}),
      }
      const response = await supportTaskApi.list(params)
      setItems(response.data.items || [])
      setTotalPages(response.data.totalPages || 1)
      setTotalCount(response.data.totalCount || 0)
    } catch (error) {
      console.error('Error loading tasks:', error)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  const loadKanban = async () => {
    try {
      setKanbanLoading(true)
      const params = {}
      if (filters.projectScope) params.projectScope = filters.projectScope
      if (filters.assignedToTaskMemberId) params.assignedToTaskMemberId = filters.assignedToTaskMemberId
      if (!showClosed) params.excludeClosed = true
      const response = await supportTaskApi.getKanban(params)
      setKanbanData(response.data)
    } catch (error) {
      console.error('Error loading kanban:', error)
    } finally {
      setKanbanLoading(false)
    }
  }

  const handleKanbanStatusChange = async (taskId, newStatus) => {
    try {
      await supportTaskApi.updateStatus(taskId, newStatus)
      await loadKanban()
    } catch (error) {
      console.error('Error updating task status:', error)
      showError(error)
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const next = { ...prev }
      if (value) {
        next[key] = value
      } else {
        delete next[key]
      }
      return next
    })
    setCurrentPage(1)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tasks"
        subtitle={viewMode === 'table' ? `${totalCount} total tasks` : undefined}
        action={
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowClosed(prev => !prev)}
              className={`p-1.5 rounded-md transition-colors ${showClosed ? 'text-gray-900 bg-gray-100' : 'text-gray-400 hover:text-gray-600'}`}
              title={showClosed ? 'Hide closed tasks' : 'Show closed tasks'}
            >
              {showClosed ? <Eye size={18} /> : <EyeOff size={18} />}
            </button>
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <List size={18} />
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'kanban' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <LayoutGrid size={18} />
              </button>
            </div>
            <Button to="/tasks/settings" variant="secondary" icon={<Settings size={18} />} />
            <Button to="/tasks/create" variant="primary" icon={<Plus size={18} />}>
              Create Task
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-col lg:flex-row gap-4">
            {viewMode === 'table' && (
              <div className="flex-1">
                <SearchBar
                  value={searchInput}
                  onChange={setSearchInput}
                  placeholder="Search tasks..."
                />
              </div>
            )}
            <FormSelect
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              options={[
                { value: '', label: 'All Statuses' },
                { value: '1', label: 'Open' },
                { value: '2', label: 'In Progress' },
                { value: '3', label: 'On Hold' },
                { value: '4', label: 'Resolved' },
                { value: '5', label: 'Closed' },
              ]}
            />
            <FormSelect
              value={filters.priority || ''}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              options={[
                { value: '', label: 'All Priorities' },
                { value: '1', label: 'Low' },
                { value: '2', label: 'Medium' },
                { value: '3', label: 'High' },
                { value: '4', label: 'Critical' },
              ]}
            />
            <FormSelect
              value={filters.taskType || ''}
              onChange={(e) => handleFilterChange('taskType', e.target.value)}
              options={[
                { value: '', label: 'All Types' },
                { value: '1', label: 'Bug' },
                { value: '2', label: 'Feature' },
                { value: '3', label: 'Improvement' },
                { value: '4', label: 'Maintenance' },
                { value: '5', label: 'Investigation' },
                { value: '6', label: 'Other' },
              ]}
            />
            <FormSelect
              value={filters.assignedToTaskMemberId || ''}
              onChange={(e) => handleFilterChange('assignedToTaskMemberId', e.target.value)}
              options={[
                { value: '', label: 'All Assignees' },
                ...assigneeOptions.filter(m => m.isActive).map(m => ({ value: String(m.taskMemberId), label: m.name }))
              ]}
            />
          </div>
        </CardBody>
      </Card>

      {/* Content */}
      {viewMode === 'kanban' ? (
        kanbanLoading ? <LoadingSpinner /> : <KanbanBoard data={kanbanData} onStatusChange={handleKanbanStatusChange} hideClosed={!showClosed} />
      ) : (
        loading ? <LoadingSpinner /> : (
          <ListContainer>
            {items.length === 0 ? (
              <EmptyState
                icon={ClipboardList}
                title="No tasks found"
                description="Get started by creating your first task."
                action={<Button to="/tasks/create" variant="primary">Create Task</Button>}
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableCell header>Task</TableCell>
                      <TableCell header>Type</TableCell>
                      <TableCell header>Priority</TableCell>
                      <TableCell header>Status</TableCell>
                      <TableCell header>Project</TableCell>
                      <TableCell header>Assigned To</TableCell>
                      <TableCell header>Due Date</TableCell>
                      <TableCell header>Created</TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((task) => (
                      <TableRow
                        key={task.supportTaskId}
                        clickable
                        onClick={() => navigate(`/tasks/${task.supportTaskId}`)}
                      >
                        <TableCell>
                          <div>
                            <span className="text-xs font-mono text-gray-500 mr-2">{task.taskNumber}</span>
                            <span className="text-sm font-medium text-gray-900">{task.title}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-700">{typeLabels[task.taskType] || '-'}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={priorityColors[task.priority]} size="small">
                            {priorityLabels[task.priority]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusColors[task.status]} size="small">
                            {statusLabels[task.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-700">{scopeFlags(task.projectScope)}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-700">{task.assignedToName || '-'}</span>
                        </TableCell>
                        <TableCell>
                          <span className={`text-sm ${task.isOverdue ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                            {formatDate(task.dueDate)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-500">{formatDate(task.createDateTimeUtc)}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                  />
                )}
              </>
            )}
          </ListContainer>
        )
      )}
    </div>
  )
}

export default Tasks
